'use client';

import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { useForm } from 'react-hook-form';
import { useAccountSettingsPageContext } from '@module/mdrt-org/account-settings/providers/account-settings-page-context';
import { PushNotificationPreferenceToggle } from '@module/mdrt-org/shared/components/notification-preference-toggle/push-notification-preference-toggle';
import { useUserContext } from '@module/mdrt-org/shared/components/providers/user-context';
import { SelectField } from '@module/mdrt-org/shared/components/select-field/select-field';
import { type InterestsData } from '@module/mdrt-org/shared/utils/data/graphql/utils/get-topics';
import { patchAccountSettingsAction } from '@module/mdrt-org/shared/utils/data/patch-account-settings-action';
import { AccountSettingsFormStep } from '@module/mdrt-org/shared/utils/enums/account-settings-form-step';
import { AccountSettingsMutationKey } from '@module/mdrt-org/shared/utils/enums/account-settings-mutation-key';
import { DevelopedFeatures } from '@module/mdrt-org/shared/utils/feature-flags/constants';
import { useFeatureFlags } from '@module/mdrt-org/shared/utils/feature-flags/use-feature-flags';
import { splitByCharacterLimit } from '@module/mdrt-org/shared/utils/helpers/split-by-character-limit';
import { useAddDefaultErrorToast } from '@module/mdrt-org/shared/utils/hooks/use-add-default-error-toast';
import { useConfirmNavigation } from '@module/mdrt-org/shared/utils/hooks/use-confirm-navigation';
import { useMutationHandler } from '@module/mdrt-org/shared/utils/hooks/use-mutation-handler';
import { type CenterCommunicationTopics, type CommunicationTopics } from '@module/mdrt-org/shared/utils/types/customer-profile';
import { Button } from '@shared/ui-components/button/button';
import { CheckboxField } from '@shared/ui-components/checkbox-field/checkbox-field';
import { FormWrapper } from '@shared/ui-components/form-wrapper/form-wrapper';
import { formBaseConfig } from '@shared/utils/constants/formBaseConfig';
import { GTM_ACTION_CLICK, GTM_EVENT_EMAILSMS_PREFERENCE_CLICK, GTM_EVENT_TOPIC_INTEREST_CLICK, GTM_EVENT_USER_INTERACTIONS } from '@shared/utils/constants/gtm';
import { ButtonType } from '@shared/utils/enums/button-type';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';
import { type SelectOption } from '@shared/utils/types/select-option';
import { type ServerActionResponse } from '@shared/utils/types/server-action';
import { type SitePreferencesData, type SitePreferencesFormData } from '../../utils/types/form-data';
import { type AccountSettingsSitePreferencesContent } from './get-account-settings-site-preferences-content';
import { InterestsSection } from './interests-section/interests-section';
import { CommunicationPreferenceCenterTypes } from './utils/get-current-preference-center';
import styles from './site-preferences.module.scss';
type SitePreferencesFormProps = {
  cmsContent: AccountSettingsSitePreferencesContent;
  languagesOptions: SelectOption[];
  tabUrl: string;
  topics: InterestsData;
};
type CommunicationTopicKeys = keyof CommunicationTopics;
type CenterCommunicationTopicsKeys = keyof CenterCommunicationTopics;
export const SitePreferencesForm = t0 => {
  const $ = _c(133);
  const {
    cmsContent,
    languagesOptions,
    tabUrl,
    topics
  } = t0;
  const {
    communicationPreferencesContextData,
    customerProfileContextData,
    resetCommunicationPreferencesContextData,
    resetCustomerProfileContextData,
    setCommunicationPreferencesContextData,
    setCustomerProfileContextData
  } = useAccountSettingsPageContext();
  const {
    personId
  } = useUserContext();
  const addErrorToast = useAddDefaultErrorToast();
  let t1;
  if ($[0] !== cmsContent.addMoreTopicsButtonLabel || $[1] !== cmsContent.cancelButtonLabel || $[2] !== cmsContent.interestModalSaveButtonLabel || $[3] !== cmsContent.interestModalSubTitle || $[4] !== cmsContent.interestModalTitle || $[5] !== cmsContent.interestsErrorMessage || $[6] !== cmsContent.interestsTitle || $[7] !== cmsContent.selectTopics) {
    t1 = {
      addMoreTopicsButtonLabel: cmsContent.addMoreTopicsButtonLabel,
      cancelButtonLabel: cmsContent.cancelButtonLabel,
      description: cmsContent.selectTopics,
      interestModalSubTitle: cmsContent.interestModalSubTitle,
      interestModalTitle: cmsContent.interestModalTitle,
      interestsErrorMessage: cmsContent.interestsErrorMessage,
      saveButtonLabel: cmsContent.interestModalSaveButtonLabel,
      title: cmsContent.interestsTitle
    };
    $[0] = cmsContent.addMoreTopicsButtonLabel;
    $[1] = cmsContent.cancelButtonLabel;
    $[2] = cmsContent.interestModalSaveButtonLabel;
    $[3] = cmsContent.interestModalSubTitle;
    $[4] = cmsContent.interestModalTitle;
    $[5] = cmsContent.interestsErrorMessage;
    $[6] = cmsContent.interestsTitle;
    $[7] = cmsContent.selectTopics;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const interestsSectionCmsContent = t1;
  const {
    getIsFeatureEnabled
  } = useFeatureFlags();
  let t2;
  if ($[9] !== getIsFeatureEnabled) {
    t2 = getIsFeatureEnabled(DevelopedFeatures.PushNotifications);
    $[9] = getIsFeatureEnabled;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const isPushNotificationsFeatureEnabled = t2;
  let t3;
  if ($[11] !== customerProfileContextData.interests || $[12] !== topics) {
    t3 = customerProfileContextData.interests ? topics.filter(topic => customerProfileContextData.interests.includes(topic.topicId)) : [];
    $[11] = customerProfileContextData.interests;
    $[12] = topics;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== customerProfileContextData.primaryLanguage || $[15] !== languagesOptions) {
    t4 = customerProfileContextData.primaryLanguage ? {
      label: languagesOptions.find(language => language?.value === customerProfileContextData.primaryLanguage)?.label,
      value: customerProfileContextData.primaryLanguage
    } : null;
    $[14] = customerProfileContextData.primaryLanguage;
    $[15] = languagesOptions;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== communicationPreferencesContextData.centerCommunicationTopics || $[18] !== communicationPreferencesContextData.communicationCenter || $[19] !== communicationPreferencesContextData.communicationTopics || $[20] !== communicationPreferencesContextData.contactPermission || $[21] !== customerProfileContextData.memberDirectoryOptIn || $[22] !== t3 || $[23] !== t4) {
    t5 = {
      ...formBaseConfig,
      defaultValues: {
        centerCommunicationTopics: communicationPreferencesContextData.centerCommunicationTopics,
        communicationCenter: communicationPreferencesContextData.communicationCenter,
        communicationTopics: communicationPreferencesContextData.communicationTopics,
        contactPermission: communicationPreferencesContextData.contactPermission,
        interests: t3,
        memberDirectoryOptIn: customerProfileContextData.memberDirectoryOptIn,
        preferredLanguage: t4
      }
    };
    $[17] = communicationPreferencesContextData.centerCommunicationTopics;
    $[18] = communicationPreferencesContextData.communicationCenter;
    $[19] = communicationPreferencesContextData.communicationTopics;
    $[20] = communicationPreferencesContextData.contactPermission;
    $[21] = customerProfileContextData.memberDirectoryOptIn;
    $[22] = t3;
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const formMethods = useForm(t5);
  const {
    formState: t6,
    reset
  } = formMethods;
  const {
    isDirty
  } = t6;
  let t7;
  if ($[25] !== addErrorToast || $[26] !== resetCommunicationPreferencesContextData || $[27] !== resetCustomerProfileContextData) {
    t7 = result => {
      if (result.status !== 200 && "message" in result) {
        resetCustomerProfileContextData();
        resetCommunicationPreferencesContextData();
        addErrorToast();
      }
    };
    $[25] = addErrorToast;
    $[26] = resetCommunicationPreferencesContextData;
    $[27] = resetCustomerProfileContextData;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const onFailure = t7;
  let t8;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [AccountSettingsMutationKey.PATCH_ACCOUNT_SETTINGS];
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== formMethods || $[31] !== reset) {
    t9 = () => reset({
      ...formMethods.getValues()
    });
    $[30] = formMethods;
    $[31] = reset;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  let t10;
  if ($[33] !== onFailure || $[34] !== t9) {
    t10 = {
      mutationFunction: patchAccountSettingsAction,
      mutationKey: t8,
      onFailure,
      onSuccessHandler: t9
    };
    $[33] = onFailure;
    $[34] = t9;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  const {
    isMutationPending,
    mutate
  } = useMutationHandler(t10);
  let t11;
  if ($[36] !== cmsContent.MDRTFoundationLabel || $[37] !== cmsContent.MDRTMeetingsLabel || $[38] !== cmsContent.MDRTStoreLabel || $[39] !== cmsContent.businessAnnouncementsLabel || $[40] !== cmsContent.contentNewsletterLabel || $[41] !== cmsContent.memberBenefitsLabel || $[42] !== communicationPreferencesContextData.communicationCenter) {
    t11 = communicationPreferencesContextData.communicationCenter === CommunicationPreferenceCenterTypes.MDRT ? [{
      label: cmsContent.memberBenefitsLabel,
      name: "communicationTopics.memberBenefits"
    }, {
      label: cmsContent.contentNewsletterLabel,
      name: "communicationTopics.contentNewsletter"
    }, {
      label: cmsContent.MDRTMeetingsLabel,
      name: "communicationTopics.mdrtMeetings"
    }, {
      label: cmsContent.businessAnnouncementsLabel,
      name: "communicationTopics.businessAnnouncements"
    }, {
      label: cmsContent.MDRTFoundationLabel,
      name: "communicationTopics.mdrtFoundation"
    }, {
      label: cmsContent.MDRTStoreLabel,
      name: "communicationTopics.mdrtStore"
    }] : communicationPreferencesContextData.communicationCenter === CommunicationPreferenceCenterTypes.Center ? [{
      label: cmsContent.memberBenefitsLabel,
      name: "centerCommunicationTopics.memberBenefits"
    }, {
      label: cmsContent.contentNewsletterLabel,
      name: "centerCommunicationTopics.contentNewsletter"
    }, {
      label: cmsContent.MDRTMeetingsLabel,
      name: "centerCommunicationTopics.mdrtMeetings"
    }, {
      label: cmsContent.businessAnnouncementsLabel,
      name: "centerCommunicationTopics.businessAnnouncements"
    }] : [];
    $[36] = cmsContent.MDRTFoundationLabel;
    $[37] = cmsContent.MDRTMeetingsLabel;
    $[38] = cmsContent.MDRTStoreLabel;
    $[39] = cmsContent.businessAnnouncementsLabel;
    $[40] = cmsContent.contentNewsletterLabel;
    $[41] = cmsContent.memberBenefitsLabel;
    $[42] = communicationPreferencesContextData.communicationCenter;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  const emailNotifications = t11;
  const hasDifferentNames = _temp3;
  const hasMismatch = _temp4;
  let t12;
  if ($[44] !== communicationPreferencesContextData.centerCommunicationTopics || $[45] !== communicationPreferencesContextData.communicationCenter || $[46] !== communicationPreferencesContextData.communicationTopics || $[47] !== communicationPreferencesContextData.contactPermission.smsPermission || $[48] !== customerProfileContextData || $[49] !== mutate || $[50] !== personId || $[51] !== setCommunicationPreferencesContextData || $[52] !== setCustomerProfileContextData || $[53] !== topics) {
    t12 = async data => {
      setCustomerProfileContextData({
        ...customerProfileContextData,
        interests: data.interests && data.interests.map(_temp5),
        memberDirectoryOptIn: data.memberDirectoryOptIn,
        primaryLanguage: data.preferredLanguage && Number(data.preferredLanguage.value)
      });
      setCommunicationPreferencesContextData({
        centerCommunicationTopics: communicationPreferencesContextData.communicationCenter === CommunicationPreferenceCenterTypes.Center ? data.communicationTopics : null,
        communicationCenter: data.communicationCenter,
        communicationTopics: communicationPreferencesContextData.communicationCenter === CommunicationPreferenceCenterTypes.MDRT ? data.communicationTopics : null,
        contactPermission: data.contactPermission
      });
      mutate({
        formData: {
          accountSettingStep: AccountSettingsFormStep.SitePreferences,
          permissionData: {
            centerCommunicationTopics: communicationPreferencesContextData.communicationCenter === CommunicationPreferenceCenterTypes.Center ? data.centerCommunicationTopics : null,
            communicationCenter: data.communicationCenter,
            communicationTopics: communicationPreferencesContextData.communicationCenter === CommunicationPreferenceCenterTypes.MDRT ? data.communicationTopics : null,
            contactPermission: data.contactPermission
          },
          sitePreferenceData: {
            interests: data.interests && data.interests.map(_temp6),
            memberDirectoryOptIn: data.memberDirectoryOptIn,
            preferredLanguageId: data.preferredLanguage && Number(data.preferredLanguage.value)
          }
        },
        personId
      });
      const savedInterests = customerProfileContextData.interests ? topics.filter(topic_0 => customerProfileContextData.interests.includes(topic_0.topicId)) : [];
      const isDirtySavedInterests = hasDifferentNames(savedInterests, data?.interests);
      let isDirtyEmailPrefernce = false;
      if (communicationPreferencesContextData.communicationCenter === "mdrtPreferenceCenter") {
        isDirtyEmailPrefernce = hasMismatch(communicationPreferencesContextData.communicationTopics, data.communicationTopics);
      } else {
        if (communicationPreferencesContextData.communicationCenter === "mdrtCenterPreferenceCenter") {
          isDirtyEmailPrefernce = hasMismatch(communicationPreferencesContextData.centerCommunicationTopics, data.centerCommunicationTopics);
        }
      }
      if (communicationPreferencesContextData.contactPermission.smsPermission !== data?.contactPermission?.smsPermission) {
        isDirtyEmailPrefernce = true;
      }
      if (customerProfileContextData.memberDirectoryOptIn !== data?.memberDirectoryOptIn) {
        isDirtyEmailPrefernce = true;
      }
      if (isDirtyEmailPrefernce) {
        let currentCommunicationTopics = data.centerCommunicationTopics;
        if (communicationPreferencesContextData.communicationCenter === "mdrtPreferenceCenter") {
          currentCommunicationTopics = data.communicationTopics;
        }
        let selectedCommunicationTopics = Object.entries(currentCommunicationTopics ?? {}).filter(_temp7).map(_temp8).join(", ");
        const additionalTopics = [];
        if (data?.contactPermission?.smsPermission) {
          additionalTopics.push("sms_notifications");
        }
        if (data?.memberDirectoryOptIn) {
          additionalTopics.push("member_directory");
        }
        if (additionalTopics.length > 0) {
          selectedCommunicationTopics = selectedCommunicationTopics ? `${selectedCommunicationTopics}, ${additionalTopics.join(", ")}` : additionalTopics.join(", ");
        }
        if (selectedCommunicationTopics?.length > 0) {
          const topicChunks = splitByCharacterLimit(selectedCommunicationTopics, 100);
          for (const chunk of topicChunks) {
            sendGtmEvent({
              action: GTM_ACTION_CLICK,
              event: GTM_EVENT_USER_INTERACTIONS,
              eventName: GTM_EVENT_EMAILSMS_PREFERENCE_CLICK,
              label: chunk
            });
          }
        }
      }
      if (isDirtySavedInterests) {
        const selctedTopicInterests = data?.interests?.map(_temp9).join(", ");
        const topicChunks_0 = splitByCharacterLimit(selctedTopicInterests, 100);
        for (const chunk_0 of topicChunks_0) {
          sendGtmEvent({
            action: GTM_ACTION_CLICK,
            event: GTM_EVENT_USER_INTERACTIONS,
            eventName: GTM_EVENT_TOPIC_INTEREST_CLICK,
            label: chunk_0
          });
        }
      }
    };
    $[44] = communicationPreferencesContextData.centerCommunicationTopics;
    $[45] = communicationPreferencesContextData.communicationCenter;
    $[46] = communicationPreferencesContextData.communicationTopics;
    $[47] = communicationPreferencesContextData.contactPermission.smsPermission;
    $[48] = customerProfileContextData;
    $[49] = mutate;
    $[50] = personId;
    $[51] = setCommunicationPreferencesContextData;
    $[52] = setCustomerProfileContextData;
    $[53] = topics;
    $[54] = t12;
  } else {
    t12 = $[54];
  }
  const handleSubmit = t12;
  useConfirmNavigation(isDirty, tabUrl);
  let t13;
  if ($[55] !== cmsContent.pushNotificationsMobileModalAcceptButtonTitle || $[56] !== cmsContent.pushNotificationsMobileModalCancelButtonTitle || $[57] !== cmsContent.pushNotificationsMobileModalDescription || $[58] !== cmsContent.pushNotificationsMobileModalTitle || $[59] !== cmsContent.pushNotificationsModalAcceptButtonTitle || $[60] !== cmsContent.pushNotificationsModalCancelButtonTitle || $[61] !== cmsContent.pushNotificationsModalDescription || $[62] !== cmsContent.pushNotificationsModalTitle) {
    t13 = {
      acceptButtonLabel: cmsContent.pushNotificationsModalAcceptButtonTitle,
      acceptButtonLabelMobile: cmsContent.pushNotificationsMobileModalAcceptButtonTitle,
      bodyText: cmsContent.pushNotificationsModalDescription,
      bodyTextMobile: cmsContent.pushNotificationsMobileModalDescription,
      cancelButtonLabel: cmsContent.pushNotificationsModalCancelButtonTitle,
      cancelButtonLabelMobile: cmsContent.pushNotificationsMobileModalCancelButtonTitle,
      headerTitle: cmsContent.pushNotificationsModalTitle,
      headerTitleMobile: cmsContent.pushNotificationsMobileModalTitle
    };
    $[55] = cmsContent.pushNotificationsMobileModalAcceptButtonTitle;
    $[56] = cmsContent.pushNotificationsMobileModalCancelButtonTitle;
    $[57] = cmsContent.pushNotificationsMobileModalDescription;
    $[58] = cmsContent.pushNotificationsMobileModalTitle;
    $[59] = cmsContent.pushNotificationsModalAcceptButtonTitle;
    $[60] = cmsContent.pushNotificationsModalCancelButtonTitle;
    $[61] = cmsContent.pushNotificationsModalDescription;
    $[62] = cmsContent.pushNotificationsModalTitle;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  const prepromptModalCmsContent = t13;
  let t14;
  if ($[64] !== cmsContent.languageLabel) {
    t14 = <h2 className={styles["site-preferences-form__heading"]}>{cmsContent.languageLabel}</h2>;
    $[64] = cmsContent.languageLabel;
    $[65] = t14;
  } else {
    t14 = $[65];
  }
  let t15;
  if ($[66] !== cmsContent.chooseLanguage) {
    t15 = <p className={styles["site-preferences-form__sub-heading"]}>{cmsContent.chooseLanguage}</p>;
    $[66] = cmsContent.chooseLanguage;
    $[67] = t15;
  } else {
    t15 = $[67];
  }
  let t16;
  if ($[68] !== cmsContent.requiredLabel) {
    t16 = {
      required: cmsContent.requiredLabel
    };
    $[68] = cmsContent.requiredLabel;
    $[69] = t16;
  } else {
    t16 = $[69];
  }
  let t17;
  if ($[70] !== cmsContent.preferredLanguageLabel || $[71] !== languagesOptions || $[72] !== t16) {
    t17 = <SelectField label={cmsContent.preferredLanguageLabel} name="preferredLanguage" options={languagesOptions} rules={t16} />;
    $[70] = cmsContent.preferredLanguageLabel;
    $[71] = languagesOptions;
    $[72] = t16;
    $[73] = t17;
  } else {
    t17 = $[73];
  }
  let t18;
  if ($[74] !== t14 || $[75] !== t15 || $[76] !== t17) {
    t18 = <div>{t14}{t15}{t17}</div>;
    $[74] = t14;
    $[75] = t15;
    $[76] = t17;
    $[77] = t18;
  } else {
    t18 = $[77];
  }
  let t19;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <hr className={styles["site-preferences-form__separator"]} />;
    $[78] = t19;
  } else {
    t19 = $[78];
  }
  let t20;
  if ($[79] !== interestsSectionCmsContent || $[80] !== topics) {
    t20 = <InterestsSection allTopics={topics} cmsContent={interestsSectionCmsContent} />;
    $[79] = interestsSectionCmsContent;
    $[80] = topics;
    $[81] = t20;
  } else {
    t20 = $[81];
  }
  let t21;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <hr className={styles["site-preferences-form__separator"]} />;
    $[82] = t21;
  } else {
    t21 = $[82];
  }
  let t22;
  if ($[83] !== cmsContent.emailNotificationLabel) {
    t22 = <h2 className={styles["site-preferences-form__heading"]}>{cmsContent.emailNotificationLabel}</h2>;
    $[83] = cmsContent.emailNotificationLabel;
    $[84] = t22;
  } else {
    t22 = $[84];
  }
  let t23;
  if ($[85] !== cmsContent.emailNotificationInformationLabel) {
    t23 = <p className={styles["site-preferences-form__content"]}>{cmsContent.emailNotificationInformationLabel}</p>;
    $[85] = cmsContent.emailNotificationInformationLabel;
    $[86] = t23;
  } else {
    t23 = $[86];
  }
  let t24;
  if ($[87] !== cmsContent.communicationTopicInformationLabel) {
    t24 = <legend className={styles["site-preferences-form__legend"]}>{cmsContent.communicationTopicInformationLabel}</legend>;
    $[87] = cmsContent.communicationTopicInformationLabel;
    $[88] = t24;
  } else {
    t24 = $[88];
  }
  let t25;
  if ($[89] !== emailNotifications) {
    t25 = emailNotifications.map(_temp10);
    $[89] = emailNotifications;
    $[90] = t25;
  } else {
    t25 = $[90];
  }
  let t26;
  if ($[91] !== t24 || $[92] !== t25) {
    t26 = <fieldset className={styles["site-preferences-form__fieldset"]}>{t24}{t25}</fieldset>;
    $[91] = t24;
    $[92] = t25;
    $[93] = t26;
  } else {
    t26 = $[93];
  }
  let t27;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = classNames(styles["site-preferences-form__content"], styles["site-preferences-form__content--reduce-spacing"]);
    $[94] = t27;
  } else {
    t27 = $[94];
  }
  let t28;
  if ($[95] !== cmsContent.communicationRemovalInformationLabel) {
    t28 = <p className={t27}>{cmsContent.communicationRemovalInformationLabel}</p>;
    $[95] = cmsContent.communicationRemovalInformationLabel;
    $[96] = t28;
  } else {
    t28 = $[96];
  }
  let t29;
  if ($[97] !== t23 || $[98] !== t26 || $[99] !== t28) {
    t29 = <div>{t23}{t26}{t28}</div>;
    $[97] = t23;
    $[98] = t26;
    $[99] = t28;
    $[100] = t29;
  } else {
    t29 = $[100];
  }
  let t30;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <hr className={styles["site-preferences-form__separator"]} />;
    $[101] = t30;
  } else {
    t30 = $[101];
  }
  let t31;
  if ($[102] !== cmsContent.SMSNotificationLabel) {
    t31 = <h2 className={styles["site-preferences-form__heading"]}>{cmsContent.SMSNotificationLabel}</h2>;
    $[102] = cmsContent.SMSNotificationLabel;
    $[103] = t31;
  } else {
    t31 = $[103];
  }
  let t32;
  if ($[104] !== cmsContent.SMSNotificationPermissionLabel) {
    t32 = <div className={styles["site-preferences-form__row"]}><CheckboxField label={cmsContent.SMSNotificationPermissionLabel} name="contactPermission.smsPermission" /></div>;
    $[104] = cmsContent.SMSNotificationPermissionLabel;
    $[105] = t32;
  } else {
    t32 = $[105];
  }
  let t33;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <hr className={styles["site-preferences-form__separator"]} />;
    $[106] = t33;
  } else {
    t33 = $[106];
  }
  let t34;
  if ($[107] !== cmsContent.memberDirectoryTitle) {
    t34 = <h2 className={styles["site-preferences-form__heading"]}>{cmsContent.memberDirectoryTitle}</h2>;
    $[107] = cmsContent.memberDirectoryTitle;
    $[108] = t34;
  } else {
    t34 = $[108];
  }
  let t35;
  if ($[109] !== cmsContent.memberDirectoryLabel) {
    t35 = <CheckboxField label={cmsContent.memberDirectoryLabel} name="memberDirectoryOptIn" />;
    $[109] = cmsContent.memberDirectoryLabel;
    $[110] = t35;
  } else {
    t35 = $[110];
  }
  let t36;
  if ($[111] !== cmsContent.pushNotificationsBlockedHint || $[112] !== cmsContent.pushNotificationsDescription || $[113] !== cmsContent.pushNotificationsTitle || $[114] !== isPushNotificationsFeatureEnabled || $[115] !== prepromptModalCmsContent) {
    t36 = isPushNotificationsFeatureEnabled && <><hr className={styles["site-preferences-form__separator"]} /><PushNotificationPreferenceToggle description={cmsContent.pushNotificationsDescription} hintLabel={cmsContent.pushNotificationsBlockedHint} id="push-notifications" modalCmsContent={prepromptModalCmsContent} title={cmsContent.pushNotificationsTitle} /></>;
    $[111] = cmsContent.pushNotificationsBlockedHint;
    $[112] = cmsContent.pushNotificationsDescription;
    $[113] = cmsContent.pushNotificationsTitle;
    $[114] = isPushNotificationsFeatureEnabled;
    $[115] = prepromptModalCmsContent;
    $[116] = t36;
  } else {
    t36 = $[116];
  }
  const t37 = !isDirty || isMutationPending;
  let t38;
  if ($[117] !== cmsContent.saveChangesButtonLabel || $[118] !== t37) {
    t38 = <div className={styles["site-preferences-form__footer"]}><Button disabled={t37} type={ButtonType.SUBMIT}>{cmsContent.saveChangesButtonLabel}</Button></div>;
    $[117] = cmsContent.saveChangesButtonLabel;
    $[118] = t37;
    $[119] = t38;
  } else {
    t38 = $[119];
  }
  let t39;
  if ($[120] !== formMethods || $[121] !== handleSubmit || $[122] !== t18 || $[123] !== t20 || $[124] !== t22 || $[125] !== t29 || $[126] !== t31 || $[127] !== t32 || $[128] !== t34 || $[129] !== t35 || $[130] !== t36 || $[131] !== t38) {
    t39 = <FormWrapper className={styles["site-preferences-form"]} dataTestId="site-preferences-form" formMethods={formMethods} onSubmit={handleSubmit}>{t18}{t19}{t20}{t21}{t22}{t29}{t30}{t31}{t32}{t33}{t34}{t35}{t36}{t38}</FormWrapper>;
    $[120] = formMethods;
    $[121] = handleSubmit;
    $[122] = t18;
    $[123] = t20;
    $[124] = t22;
    $[125] = t29;
    $[126] = t31;
    $[127] = t32;
    $[128] = t34;
    $[129] = t35;
    $[130] = t36;
    $[131] = t38;
    $[132] = t39;
  } else {
    t39 = $[132];
  }
  return t39;
};
function _temp(item) {
  return item.name;
}
function _temp2(item_0) {
  return item_0.name;
}
function _temp3(array1, array2) {
  if (array1.length !== array2.length) {
    return true;
  }
  const names1 = array1.map(_temp).sort();
  const names2 = array2.map(_temp2).sort();
  return names1.some((name, index) => name !== names2[index]);
}
function _temp4(object1, object2) {
  if (!object1 || !object2) {
    return true;
  }
  const keys = new Set([...Object.keys(object1), ...Object.keys(object2)]);
  for (const key of keys) {
    if (object1[key] !== object2[key]) {
      return true;
    }
  }
  return false;
}
function _temp5(interest) {
  return interest.topicId;
}
function _temp6(interest_0) {
  return interest_0.topicId;
}
function _temp7(t0) {
  const [, value] = t0;
  return value;
}
function _temp8(t0) {
  const [key_0] = t0;
  return key_0;
}
function _temp9(item_1) {
  return item_1?.name;
}
function _temp10(emailNotification) {
  return <CheckboxField key={emailNotification.label} label={emailNotification.label} name={emailNotification.name} />;
}